import React, { useEffect, useState } from "react";
import {
  Grid,
  Paper,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Typography,
  TextField,
  Card,
  CardContent,
  Divider,
} from "@mui/material";

import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RoomIcon from "@mui/icons-material/Room";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import SaveIcon from "@mui/icons-material/Save";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimeField } from "@mui/x-date-pickers/TimeField";

import CircularProgress from "@mui/material/CircularProgress";
import dayjs from "dayjs";

import CustomAlert from "../../Common/Components/CustomAlert";

function TabWebTime({ emplacement, onSave }) {
  const { t } = useTranslation(["Articles"]);
  const userState = useSelector((state) => state.user);

  const [formData, setFormData] = useState({
    // Location
    location: "",

    // Services
    dinein: false,
    takeout: false,
    delivery: false,
    meetingRoom: false,
    terrace: false,

    // Hours
    mondayOpen: null,
    mondayClose: null,

    tuesdayOpen: null,
    tuesdayClose: null,

    wednesdayOpen: null,
    wednesdayClose: null,

    thursdayOpen: null,
    thursdayClose: null,

    fridayOpen: null,
    fridayClose: null,

    saturdayOpen: null,
    saturdayClose: null,

    sundayOpen: null,
    sundayClose: null,
  });

  const [loading, setLoading] = useState(false);

  const [alert, setAlert] = useState({
    show: false,
    type: "",
    message: "",
  });

  useEffect(() => {
    if (!emplacement) return;

    setFormData({
      location: emplacement.location || "",

      dinein: emplacement.dinein || false,
      takeout: emplacement.takeout || false,
      delivery: emplacement.delivery || false,
      meetingRoom: emplacement.meeting_room || false,
      terrace: emplacement.terrace || false,

      mondayOpen: getTime(emplacement.monday_open),
      mondayClose: getTime(emplacement.monday_close),

      tuesdayOpen: getTime(emplacement.tuesday_open),
      tuesdayClose: getTime(emplacement.tuesday_close),

      wednesdayOpen: getTime(emplacement.wednesday_open),
      wednesdayClose: getTime(emplacement.wednesday_close),

      thursdayOpen: getTime(emplacement.thursday_open),
      thursdayClose: getTime(emplacement.thursday_close),

      fridayOpen: getTime(emplacement.friday_open),
      fridayClose: getTime(emplacement.friday_close),

      saturdayOpen: getTime(emplacement.saturday_open),
      saturdayClose: getTime(emplacement.saturday_close),

      sundayOpen: getTime(emplacement.sunday_open),
      sundayClose: getTime(emplacement.sunday_close),
    });
  }, [emplacement]);

  const getTime = (value) => {
    if (!value) return null;

    return dayjs(value, "HH:mm:ss");
  };

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]:
        event.target.type === "checkbox"
          ? event.target.checked
          : event.target.value,
    }));
  };

  const handleTimeChange = (field) => (value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    setLoading(true);

    const saveData = {
      noEmplacement: emplacement?.noEmplacement,
      noAdresse: emplacement?.noAdresse,

      location: formData.location,

      dinein: formData.dinein,
      delivery: formData.delivery,
      takeout: formData.takeout,
      meetingRoom: formData.meetingRoom,
      terrace: formData.terrace,

      monday_open: formData.mondayOpen,
      monday_close: formData.mondayClose,

      tuesday_open: formData.tuesdayOpen,
      tuesday_close: formData.tuesdayClose,

      wednesday_open: formData.wednesdayOpen,
      wednesday_close: formData.wednesdayClose,

      thursday_open: formData.thursdayOpen,
      thursday_close: formData.thursdayClose,

      friday_open: formData.fridayOpen,
      friday_close: formData.fridayClose,

      saturday_open: formData.saturdayOpen,
      saturday_close: formData.saturdayClose,

      sunday_open: formData.sundayOpen,
      sunday_close: formData.sundayClose,
    };

    const result = await onSave(saveData, userState.token, 3);

    setTimeout(() => {
      setAlert({
        show: true,

        type: result.success ? "success" : "error",

        message: result.success
          ? "Web Time saved successfully"
          : "Error while saving Web Time",
      });

      setLoading(false);
    }, 1000);
  };

  const days = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  return (
    <Paper
      elevation={4}
      sx={{
        mx: "5%",
        mt: 3,
        borderRadius: 3,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          background: "#1976d2",
          color: "white",
          p: 3,
        }}
      >
        <Typography
          variant="h5"
          fontWeight="bold"
          display="flex"
          alignItems="center"
          gap={1}
        >
          <AccessTimeIcon />

          {t("WebTime")}
        </Typography>
      </Box>

      <Box sx={{ p: 4 }}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              fontWeight="bold"
              display="flex"
              gap={1}
              alignItems="center"
            >
              <RestaurantIcon />
              Services
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Grid container spacing={2}>
              {[
                ["dinein", "DineIn"],
                ["delivery", "Delivery"],
                ["takeout", "TakeOut"],
                ["meetingRoom", "MeetingRoom"],
                ["terrace", "Terrace"],
              ].map(([field, label]) => (
                <Grid item xs={12} sm={6} md={3} key={field}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formData[field]}
                        onChange={handleChange(field)}
                      />
                    }
                    label={t(label)}
                  />
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography
              variant="h6"
              fontWeight="bold"
              display="flex"
              alignItems="center"
              gap={1}
            >
              <AccessTimeIcon />
              {t("OpeningHours")}
            </Typography>

            <Divider sx={{ my: 3 }} />

            <LocalizationProvider dateAdapter={AdapterDayjs}>
              {days.map((day) => (
                <Box
                  key={day}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    mb: 2,
                    gap: 2,
                  }}
                >
                  {/* Day */}
                  <Typography
                    fontWeight="bold"
                    sx={{
                      width: 130,
                    }}
                  >
                    {t(day)}
                  </Typography>

                  {/* Open */}
                  <TimeField
                    size="small"
                    label={t("From")}
                    value={formData[`${day.toLowerCase()}Open`]}
                    onChange={handleTimeChange(`${day.toLowerCase()}Open`)}
                    sx={{
                      width: 140,
                    }}
                  />

                  <Typography>→</Typography>

                  {/* Close */}
                  <TimeField
                    size="small"
                    label={t("To")}
                    value={formData[`${day.toLowerCase()}Close`]}
                    onChange={handleTimeChange(`${day.toLowerCase()}Close`)}
                    sx={{
                      width: 140,
                    }}
                  />
                </Box>
              ))}
            </LocalizationProvider>
          </CardContent>
        </Card>

        <Button
          variant="contained"
          startIcon={
            loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SaveIcon />
            )
          }
          disabled={loading}
          onClick={handleSave}
          sx={{
            mt: 4,
            px: 5,
            height: 45,
            borderRadius: 2,
            background: "#1976d2",
            "&:hover": {
              background: "#125ca1",
            },
          }}
        >
          {loading ? "Saving..." : t("Save")}
        </Button>
      </Box>

      <CustomAlert
        open={alert.show}
        closeMessage={() =>
          setAlert((prev) => ({
            ...prev,
            show: false,
          }))
        }
        typeMessage={alert.type}
        message={alert.message}
      />
    </Paper>
  );
}

export default TabWebTime;
